import React, {
  createContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
  useCallback,
} from "react";
import PartySocket from "partysocket";
import { Message } from "../../types/message";
import { Option } from "../../types/option";
import {
  getConversationHistory,
  getDomainDetails,
  getLastActiveConversation,
  getSessionDetails,
  createNewConversation,
  getGEOData,
} from "../../services";
import { ColorShades, generateColorShades } from "../../utils/color";
import { useIframeDocument } from "../IframeDocumentContext";
import {
  useHMSActions,
  useHMSStore,
  selectIsConnectedToRoom,
  useHMSNotifications,
  HMSNotificationTypes,
} from "@100mslive/react-sdk";
// @ts-ignore
const VITE_SOCKET_URL = import.meta.env.VITE_SOCKET_URL;

interface ChatContextProps {
  appState: any;
  setAppState: React.Dispatch<React.SetStateAction<any>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleWindowClose: () => void;
  lastMessage: any;
  setLastMessage: React.Dispatch<React.SetStateAction<boolean>>;
  messages: Message[];
  sendMessage: (message: Message) => void;
  conversationLoading: boolean;
  setConversationLoading: (isLoading: boolean) => void;
  typingIndicator: Message | null;
  sendTypingIndicator: (isTyping: boolean) => void;
  handleOptionSelect: (option: Option) => void;
  currentConversation: string | null;
  setCurrentConversation: (cid: any) => void;
  conversationDetails: any | null;
  setConversationDetails: (c: any) => void;
  goToConversation: (data: any) => void;
  acceptCall: () => void;
  rejectCall: () => void;
  finishCall: () => void;
  callState: any;
  primaryColor: any;
}

export const ChatContext = createContext<ChatContextProps>({
  appState: {},
  setAppState: () => {},
  open: false,
  setOpen: () => {},
  handleWindowClose: () => {},
  lastMessage: null,
  setLastMessage: () => {},
  messages: [],
  sendMessage: () => {},
  conversationLoading: true,
  setConversationLoading: () => {},
  typingIndicator: null,
  sendTypingIndicator: () => {},
  handleOptionSelect: () => {},
  currentConversation: null,
  conversationDetails: null,
  setCurrentConversation: () => {},
  setConversationDetails: () => {},
  goToConversation: () => {},
  acceptCall: () => {},
  rejectCall: () => {},
  finishCall: () => {},
  callState: {},
  primaryColor: null,
});

export const ChatContextProvider = ({
  config,
  children,
}: {
  config: any;
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [appState, setAppState] = useState<any>({
    currentScreen: "home",
    previousScreen: "home",
  });

  const [lastMessage, setLastMessage] = useState<any>(null);
  const [primaryColor, setPrimaryColor] = useState<any>(null);
  const [GEOData, setGEOData] = useState<any>(null);
  const callNotification = useHMSNotifications();
  const [messages, setMessages] = useState<Message[]>([]);
  const [appConfig, setAppConfig] = useState<any>(config);
  const [callState, setCallState] = useState<any>({
    state: null,
  });

  const initialTypingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [typingIndicator, setTypingIndicator] = useState<Message | null>(null);
  const [conversationLoading, setConversationLoading] = useState<boolean>(true);
  const [currentConversation, setCurrentConversation] = useState<any>(null);
  const [conversationDetails, setConversationDetails] = useState<any>(null);

  const client = useRef<{ socket: PartySocket; room: string } | null>(null);
  const commonSocket = useRef<{ socket: PartySocket; room: string } | null>(
    null
  );
  let audio = useRef<any>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);

  const currentConversationPartnerRef = useRef<any>(config.mx_uid);
  const { iframeDocument, class_name }: any = useIframeDocument();

  useEffect(() => {
    const mx_uid = window.localStorage.getItem("mx_uid");
    const mx_cid = window.localStorage.getItem("mx_cid");
    const li_md5 = window.localStorage.getItem("mx_lim");
    const li_sha256 = window.localStorage.getItem("mx_lis");
    const td_md5 = window.localStorage.getItem("mx_tdm");
    const referrer = window.localStorage.getItem("mx_referrer");

    setAppConfig({
      ...appConfig,
      ...config,
      mx_uid,
      li_md5,
      li_sha256,
      td_md5,
      referrer,
    });
    console.log("Happened here 1", mx_cid, appConfig, config);
    setCurrentConversation(mx_cid);
  }, []);

  const sendPingToServer = useCallback(() => {
    if (commonSocket.current) {
      commonSocket.current.socket.send(
        JSON.stringify({
          type: "visitor",
          config,
          geo_data: GEOData,
          mx_cid: currentConversation,
        })
      );
      console.log("Ping sent to server");
    }
  }, [config, GEOData, currentConversation]);

  const startBotTypingIndicator = () => {
    // Set a 500ms delay before showing the typing indicator
    console.log(
      "Current agent",
      currentConversationPartnerRef.current?.metadata
    );
    initialTypingTimeoutRef.current = setTimeout(() => {
      setTypingIndicator({
        sender_type: "bot",
        sender: null,
        metadata: currentConversationPartnerRef.current?.metadata,
        content: "",
        createdAt: new Date(),
      });

      typingTimeoutRef.current = setTimeout(() => {
        startBotTypingIndicator();
      }, 2500);
    }, 500); // 500 milliseconds delay
  };

  const stopBotTypingIndicator = useCallback(() => {
    setTypingIndicator(null);
    if (initialTypingTimeoutRef.current) {
      clearTimeout(initialTypingTimeoutRef.current);
      initialTypingTimeoutRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    let intervalId: any = null;

    const startPinging = () => {
      intervalId = setInterval(sendPingToServer, 5000);
    };

    const stopPinging = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    if (
      document.visibilityState === "visible" &&
      GEOData &&
      commonSocket.current
    ) {
      startPinging();
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPinging();
      } else {
        stopPinging();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      stopPinging();
    };
  }, [GEOData, sendPingToServer, commonSocket.current]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const domainDetails = await getDomainDetails(appConfig);
        setAppState((prev: any) => ({
          ...prev,
          ...domainDetails,
          ...appConfig,
        }));

        if (!iframeDocument) {
          console.warn("Iframe reference is not available.");
          return;
        }
        const geoData = await getGEOData();
        setGEOData(geoData);
        window.localStorage.setItem(
          "max_widget_config",
          JSON.stringify(domainDetails)
        );
        setPrimaryColor(domainDetails.primaryColor);
        const primaryShades: ColorShades = generateColorShades(
          domainDetails.primaryColor || "#0d031b"
        );
        const secondaryShades: ColorShades = generateColorShades(
          domainDetails.secondaryColor || ""
        );

        const setCSSVariables = (paletteName: string, shades: ColorShades) => {
          Object.entries(shades).forEach(([shade, color]) => {
            iframeDocument?.documentElement?.style?.setProperty(
              `--color-${paletteName}-${shade}`,
              color // This should be in "r g b" format
            );
          });
        };

        // Set CSS variables for each palette
        setCSSVariables("primary", primaryShades);
        setCSSVariables("secondary", secondaryShades);
        iframeDocument?.documentElement?.style?.setProperty(
          `--color-background`,
          "248 247 245"
        );
        iframeDocument?.documentElement?.style?.setProperty(
          `--color-primary`,
          domainDetails.primaryColor || "#0d031b"
        );
        iframeDocument?.documentElement?.style?.setProperty(
          `--color-background`,
          "248 247 245"
        );

        const gooberStyleTag = document.getElementById("_goober");
        if (gooberStyleTag && iframeDocument.head) {
          const clonedStyle = gooberStyleTag.cloneNode(true);
          iframeDocument.head.appendChild(clonedStyle);
        }
        if (!open) {
          // const sessionDetailsResponse = await getSessionDetails(appConfig);
          // setAppState((prev: any) => ({
          //   ...prev,
          //   ...sessionDetailsResponse,
          //   ...appConfig,
          // }));
          // setCurrentConversation(sessionDetailsResponse?.cid);
          // if (sessionDetailsResponse?.messages?.length > 0) {
          //   window.parent.postMessage(
          //     { type: "MAXIMISE_CHAT_WIDGET_ALERT", ...sessionDetailsResponse },
          //     "*"
          //   );
          //   playIncomingNotificationSound();
          // }
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchDetails();
  }, [appConfig]);

  useEffect(() => {
    if (!callNotification) {
      return;
    }
    console.log("callNotification", callNotification);
    switch (callNotification.type) {
      case HMSNotificationTypes.PEER_JOINED:
        console.log(`${callNotification.data.name} joined`);
        break;
      case HMSNotificationTypes.PEER_LEFT:
        console.log(`${callNotification.data.name} left`);
        break;
      case HMSNotificationTypes.NEW_MESSAGE:
        console.log(
          `${callNotification.data.message} received from ${callNotification.data.senderName}`
        );
        break;
      case HMSNotificationTypes.ERROR:
        console.log("[Error]", callNotification.data);
        console.log("[Error Code]", callNotification.data.code);
        break;
      case HMSNotificationTypes.RECONNECTING:
        console.log("[Reconnecting]", callNotification.data);
        break;
      case HMSNotificationTypes.RECONNECTED:
        console.log("[Reconnected]");
        break;
      case HMSNotificationTypes.NAME_UPDATED:
      case HMSNotificationTypes.METADATA_UPDATED:
      case HMSNotificationTypes.ROLE_UPDATED:
        console.log(
          `peer updated(${callNotification.type}), new peer=`,
          callNotification.data
        );
        break;
      case HMSNotificationTypes.TRACK_DEGRADED:
        console.log(
          `track - ${callNotification.data} degraded due to poor network`
        );
        break;
      case HMSNotificationTypes.TRACK_RESTORED:
        console.log(`track - ${callNotification.data} recovered`);
        break;
      case HMSNotificationTypes.ROOM_ENDED:
        console.log(`room ended, reason - ${callNotification.data.reason}`);
        setCallState({ state: null });
        break;
      case HMSNotificationTypes.REMOVED_FROM_ROOM:
        console.log(
          `removed from room, reason - ${callNotification.data.reason}`
        );
        break;
      case HMSNotificationTypes.DEVICE_CHANGE_UPDATE:
        console.log(`device changed - ${callNotification.data}`);
        break;
      default:
        break;
    }
  }, [callNotification, currentConversation]);

  const handleWindowClose = () => {
    window.parent.postMessage(
      {
        type: "MAXIMISE_CHAT_WIDGET_CLOSE",
        messages:
          messages?.length > 0
            ? messages?.slice(-1)
            : conversationDetails?.messages?.slice(-1),
      },
      "*"
    );
  };

  useEffect(() => {
    const handleMessage = (event: any) => {
      console.log(event.data, appState, currentConversation, currentConversationPartnerRef.current);
      if (appState) {
        if (event.data && event.data.type === "MAXIMISE_CHAT_WIDGET_OPEN") {
          setOpen(true);
          if (event.data?.cid && currentConversation) {
            goToConversation({cid: currentConversation})
          }
        } else if (
          event.data &&
          event.data.type === "MAXIMISE_CHAT_WIDGET_CLOSE"
        ) {
          setOpen(false);
        } else if (
          event.data &&
          event.data.type === "MAXIMISE_CHAT_WIDGET_ALERT_CLOSE" &&
          event.data?.cid
        ) {
          setOpen(true);
          setMessages([]);
          setAppState({
            ...appState,
            currentScreen: "chat",
          });
          setCurrentConversation(event.data?.cid);
          console.log(appState.currentScreen);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [appState, currentConversation]);

  useEffect(() => {
    if (appState.currentScreen === "home") {
      getLastActiveConversation({
        ...config,
        geo_data: GEOData,
      })
        .then((r) => {
          setLastMessage(r);
          setConversationDetails(r);
          setCurrentConversation(r.cid);
          currentConversationPartnerRef.current = {
            metadata: r?.current_agent,
          };
          if (!open) {
            window.parent.postMessage(
              {
                type: "MAXIMISE_MESSAGES_ALERT",
                messages: r.messages,
              },
              "*"
            );
          }
        })
        .catch(() => {
          setLastMessage(null);
          setConversationDetails(null);
          setCurrentConversation(null);
          currentConversationPartnerRef.current = null;
        });
    }

    console.log("open", open, appState);

    // if (!open) {
    //   getSessionDetails({ ...appConfig, mx_cid: currentConversation }).then(
    //     (sessionDetailsResponse) => {
    //       setAppState((prev: any) => ({
    //         ...prev,
    //         ...sessionDetailsResponse,
    //         ...appConfig,
    //       }));
    //       console.log("Happened here 4", sessionDetailsResponse?.cid);
    //       if (sessionDetailsResponse?.cid) {
    //         window.localStorage.setItem("mx_cid", sessionDetailsResponse?.cid);
    //       }
    //       if (sessionDetailsResponse?.messages?.length > 0) {
    //         window.parent.postMessage(
    //           { type: "MAXIMISE_CHAT_WIDGET_ALERT", ...sessionDetailsResponse },
    //           "*"
    //         );
    //         playIncomingNotificationSound();
    //       }
    //     }
    //   );
    // }
  }, [appState.currentScreen]);

  const clearAllTypingTimeouts = () => {
    if (initialTypingTimeoutRef.current) {
      clearTimeout(initialTypingTimeoutRef.current);
      initialTypingTimeoutRef.current = null;
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    console.log("class_name", class_name);
    if (
      currentConversation &&
      (!client.current || client.current.room !== currentConversation)
    ) {
      if (client.current) {
        return;
      }
      console.log("currentConversation", currentConversation);
      const socket = new PartySocket({
        host: VITE_SOCKET_URL,
        room: currentConversation,
      });

      client.current = { socket, room: currentConversation };

      socket.addEventListener("open", () => {
        console.log("Connected to WebSocket");
      });

      socket.addEventListener("message", (e: any) => {
        const data: Message | any = JSON.parse(e.data);
        console.log("Received message:", data);

        if (data.type === "call") {
          setCallState({ ...data });
        } else {
          if (data.is_typing) {
            handleTypingIndicator(data);
          } else {
            if (
              data.sender_type === "bot" ||
              data.sender_type === "agent" ||
              data.sender_type === "system"
            ) {
              stopBotTypingIndicator();
            } else {
              setTypingIndicator(null);
              clearTypingTimeout();
            }
          }

          if (data.sender_type === "bot" || data.sender_type === "agent") {
            const newPartner = data;
            if (
              newPartner &&
              currentConversationPartnerRef.current &&
              newPartner?.metadata?.uid !==
                currentConversationPartnerRef.current?.metadata?.uid
            ) {
              const systemMessage: Message = {
                sender_type: "system",
                sender: null,
                content:
                  newPartner?.sender_type === "agent"
                    ? `You are now chatting with ${data.metadata?.first_name}`
                    : `You are now chatting with ${data.metadata?.first_name}`,
                createdAt: new Date(),
              };
              addMessage(systemMessage);
              console.log("New partner", data);
              currentConversationPartnerRef.current = newPartner;
            }
          }

          removeLastOptions();
          // @ts-ignore
          if (data.content?.length > 0 || data.attachments?.length > 0) {
            playIncomingNotificationSound();
            console.log("data.content", data.content);
            // @ts-ignore
            if (data.content?.length > 0) {
              if (data.sender_type !== "system" && !data.is_typing) {
                addMessage({ ...data, attachments: [] });
                if (!open) {
                  window.parent.postMessage(
                    {
                      type: "MAXIMISE_MESSAGES_ALERT",
                      messages: [{ ...data, attachments: [] }],
                    },
                    "*"
                  );
                }
              }
            }
            // @ts-ignore
            if (data.attachments?.length > 0) {
              if (data.sender_type !== "system" && !data.is_typing) {
                addMessage({ ...data, content: "" });
                if (!open) {
                  window.parent.postMessage(
                    {
                      type: "MAXIMISE_MESSAGES_ALERT",
                      messages: [{ ...data, content: "" }],
                    },
                    "*"
                  );
                }
              }
            }
          }
        }
      });

      socket.addEventListener("close", () => {
        console.log("WebSocket closed");
      });

      socket.addEventListener("error", (err: any) => {
        console.error("WebSocket error:", err);
      });
    }

    return () => {
      if (client.current) {
        client.current.socket.close();
        client.current = null;
      }
      clearAllTypingTimeouts();
    };
  }, [currentConversation, open]);

  useEffect(() => {
    console.log("class_name", class_name);
    if (commonSocket.current) {
      return;
    }
    console.log("currentConversation", currentConversation);
    const socket = new PartySocket({
      host: VITE_SOCKET_URL,
      room: config?.tid,
    });

    commonSocket.current = { socket, room: currentConversation };

    socket.addEventListener("open", () => {
      console.log("Connected to Commonsocket");
    });

    socket.addEventListener("message", (e: any) => {
      const data: Message | any = JSON.parse(e.data);
      console.log("Received message:", data);
    });

    socket.addEventListener("close", () => {
      console.log("Commonsocket closed");
    });

    socket.addEventListener("error", (err: any) => {
      console.error("Commonsocket error:", err);
    });

    return () => {
      if (commonSocket.current) {
        commonSocket.current.socket.close();
        commonSocket.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (callState?.state === "incoming") {
      window.parent.postMessage(
        {
          ...callState,
          token: null,
          type: "MAXIMISE_CHAT_WIDGET_CALL_INCOMING",
        },
        "*"
      );
      if (!audio.current) {
        audio.current = new Audio(
          "https://s3.enrich.so/widget/incoming-call.mp3"
        );
        audio.current.volume = 0.5;
        audio.current.loop = true;
        audio.current
          .play()
          .catch((error: any) => console.error("Error playing sound:", error));
      }
    } else if (callState?.state === "accepted") {
      window.parent.postMessage(
        { type: "MAXIMISE_CHAT_WIDGET_CALL_CONNECTED" },
        "*"
      );
      if (audio.current) {
        audio.current.pause();
        audio.current = null;
      }
    } else if (callState?.state === "rejected") {
      window.parent.postMessage(
        { type: "MAXIMISE_CHAT_WIDGET_CALL_DISCONNECTED" },
        "*"
      );
      if (audio.current) {
        audio.current.pause();
        audio.current = null;
      }
    } else {
      window.parent.postMessage(
        { type: "MAXIMISE_CHAT_WIDGET_CALL_DISCONNECTED" },
        "*"
      );
      if (audio.current) {
        audio.current.pause();
        audio.current = null;
      }
    }
  }, [callState?.state, open]);

  useEffect(() => {
    if (!open) {
      window.parent.postMessage(
        { type: "MAXIMISE_TYPING_ALERT", typingIndicator },
        "*"
      );
    }
  }, [typingIndicator, open]);

  const addMessage = (message: Message) => {
    setMessages((prev) => [...prev, message]);
  };

  const sendMessage = (data: Message) => {
    if (!client.current) return;
    client.current.socket.send(
      JSON.stringify({
        ...data,
        ...config,
        geo_data: GEOData,
        metadata: {
          first_name: "User",
          uid: config.mx_uid,
          ...data.metadata,
        },
        mx_cid: currentConversation,
        current_time: new Date(),
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );
    // @ts-ignore
    if (data.content?.length > 0 || data.attachments?.length > 0) {
      // @ts-ignore
      if (data.content?.length > 0) {
        addMessage({ ...data, attachments: [] });
      }
      // @ts-ignore
      if (data.attachments?.length > 0) {
        addMessage({ ...data, content: "" });
      }
    }
    if (currentConversationPartnerRef.current?.metadata?.is_bot) {
      startBotTypingIndicator();
    }
  };

  const sendTypingIndicator = (isTyping: boolean) => {
    if (!client.current) return;

    const typingIndicatorMessage: Message = {
      // @ts-ignore
      metadata: {
        first_name: "User",
        uid: config.mx_uid,
      },
      is_typing: isTyping,
      sender_type: "user",
      sender: null,
      createdAt: new Date(),
      mx_cid: currentConversation,
      ...config,
      current_time: new Date(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    client.current.socket.send(
      JSON.stringify({ ...typingIndicatorMessage, mx_cid: currentConversation })
    );
  };

  const removeLastOptions = () => {
    setMessages((prev) => {
      for (let i = prev.length - 1; i >= 0; i--) {
        const message = prev[i];
        if (
          (message.sender_type === "bot" || message.sender_type === "agent") &&
          message.options
        ) {
          console.log(`Removing options from message at index ${i}:`, message);
          const updatedMessage: Message = { ...message, options: undefined };
          const newMessages = [...prev];
          newMessages[i] = updatedMessage;
          console.log(`Updated message:`, updatedMessage);
          return newMessages;
        }
      }
      console.log("No bot/agent message with options found to remove.");
      return prev;
    });
  };

  const stopAudio = () => {
    if (audio.current) audio.current?.pause();
    audio.current = null;
  };

  const playIncomingNotificationSound = () => {
    if (audio.current) audio.current?.pause();
    audio.current = new Audio(
      "https://s3.enrich.so/widget/incoming_message.wav"
    );
    audio.current.volume = 0.5;
    audio.current
      .play()
      .catch((error: any) => console.error("Error playing sound:", error));
  };

  const handleOptionSelect = (option: Option) => {
    console.log("User selected option:", option);
    const userMessage: Message = {
      // @ts-ignore
      metadata: {
        first_name: "User",
      },
      sender: null,
      content: option.label,
      content_metadata: `User has performed action ${option?.label}`,
      action: option.action,
      createdAt: new Date(),
      current_time: new Date(),
      ...config,
      mx_cid: currentConversation,
      sender_type: "user",
      geo_data: GEOData,
    };

    sendMessage(userMessage);
    removeLastOptions();
    if (option?.action !== null && option?.type === "url") {
      window.open(option?.action, "_blank");
    }
  };

  const handleTypingIndicator = (data: Message) => {
    setTypingIndicator(data);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setTypingIndicator(null);
      typingTimeoutRef.current = null;
    }, 2500); // 2.5 seconds
  };

  const clearTypingTimeout = () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  const goToConversation = async (data: any) => {
    try {
      setConversationLoading(true);
      setMessages([]);
      const payload = {
        ...appState,
        currentScreen: "chat",
        previousScreen: appState.currentScreen,
        ...data,
      };
      delete payload.max_cid;
      setAppState(payload);
      console.log(data);
      if (data?.cid) {
        setCurrentConversation(data?.cid);
        getConversationHistory({
          ...config,
          mx_cid: data?.cid,
        })
          .then((r) => {
            setConversationDetails(r);
            setMessages([...r?.messages]);
            setConversationLoading(false);
          })
          .catch(() => {
            setMessages([]);
            setConversationDetails(null);
            setConversationLoading(false);
          });
      } else {
        const newConversationPayload = {
          ...data,
          ...config,
          sender_type: "user",
          geo_data: GEOData,
        };
        console.log("newConversationPayload", newConversationPayload);
        const resp = await createNewConversation(newConversationPayload);
        if (resp.cid && resp.messages?.length > 0) {
          setCurrentConversation(resp.cid);
          setMessages([...resp?.messages]);
          setConversationLoading(false);
        }
      }
    } catch (e) {}
  };

  const acceptCall = () => {
    if (!client.current) return;
    client.current.socket.send(
      JSON.stringify({ ...callState, sender_type: "user", state: "accepted" })
    );
    setCallState({
      ...callState,
      token: null,
      sender_type: "user",
      state: "accepted",
    });
    joinRoom(callState.token, "User");
    if (audio.current) {
      audio.current.pause();
      audio.current = null;
    }
  };

  const rejectCall = () => {
    if (!client.current) return;
    client.current.socket.send(
      JSON.stringify({
        ...callState,
        token: null,
        sender_type: "user",
        state: "rejected",
      })
    );
    setCallState(null);
    if (audio.current) {
      audio.current.pause();
      audio.current = null;
    }
    leaveCall();
  };

  const finishCall = () => {
    if (!client.current) return;
    client.current.socket.send(
      JSON.stringify({
        ...callState,
        token: null,
        sender_type: "user",
        state: "finished",
      })
    );
    setCallState({ state: null });
    leaveCall();
  };

  useEffect(() => {
    const handleBeforeUnload = (e: any) => {
      if (isConnected) {
        e?.preventDefault();
        finishCall();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [finishCall]);

  const joinRoom = useCallback(
    async (authToken: string, userName: string) => {
      await hmsActions.join({
        userName,
        authToken,
      });
    },
    [hmsActions]
  );

  const leaveCall = useCallback(() => {
    hmsActions.endRoom(false, "Meeting over");
  }, []);

  return (
    <ChatContext.Provider
      value={{
        appState,
        setAppState,
        open,
        setOpen,
        handleWindowClose,
        lastMessage,
        setLastMessage,
        messages,
        sendMessage,
        conversationLoading,
        setConversationLoading,
        typingIndicator,
        sendTypingIndicator,
        handleOptionSelect,
        currentConversation,
        setCurrentConversation,
        conversationDetails,
        setConversationDetails,
        goToConversation,
        acceptCall,
        rejectCall,
        finishCall,
        callState,
        primaryColor,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
